<template>
  <div id="Admin">
    <!-- Message Banner -->
    <div id="messages" v-if="msg">{{ msg }}</div>

    <!-- Header -->
    <div class="admin-header">
      <h1>Admin Panel</h1>
      <div class="pw-row">
        <label>Password:</label>
        <input type="password" v-model="password" placeholder="Password" />
      </div>
    </div>

    <!-- Tab Navigation -->
    <div class="tab-nav">
      <div
        v-for="tab in tabs"
        :key="tab.id"
        :class="['tab', { active: activeTab === tab.id }]"
        @click="switchTab(tab.id)"
      >
        {{ tab.label }}
      </div>
    </div>

    <!-- ═══════════ TAB: Room Setup ═══════════ -->
    <div v-show="activeTab === 'setup'" class="tab-content">
      <div class="card">
        <h2>Create new rooms</h2>
        <div class="field">
          <label>Room Name</label>
          <input type="text" v-model="roomCode" placeholder="e.g. Session_01" />
        </div>

        <div id="PlayerButtonHolder">
          <div class="btn pointer" @click="addPlayer(1)">+1 Player</div>
          <div class="btn pointer" @click="addPlayer(5)">+5</div>
          <div class="btn pointer" @click="addPlayer(10)">+10</div>
          <div class="btn pointer" @click="addPlayer(15)">+15</div>
          <div v-if="players.length > 0" class="btn btn-red pointer" @click="players = []">Clear</div>
        </div>

        <p class="hint" v-if="players.length > 0">Click on the code to copy the link</p>

        <div class="player-list" v-if="players.length > 0">
          <div
            :class="['player-row', { pointer: p.token }]"
            v-for="(p, index) in players"
            :key="index"
            @click="p.token ? kopieren(index) : null"
          >
            <span class="player-id">{{ p.name }}</span>
            <div v-if="p.token" class="player-name">{{ p.nameAlt }}</div>
            <input v-if="!p.token" type="text" v-model="players[index].nameAlt" placeholder="Name" class="player-input" />
            <div v-if="p.token" class="output">
              {{ textFilter(p.token) }}
            </div>
          </div>
        </div>

        <div class="btn btn-primary btn-wide pointer" @click="erstellen" v-if="roomCode.length >= 2">
          ERSTELLEN
        </div>

        <div v-if="buyerToken" id="buyer" @click="kopieren('GM')">
          <div class="gm-title">Gamemaster</div>
          <div class="gm-code">{{ textFilter(buyerToken) }}</div>
        </div>
      </div>
    </div>

    <!-- ═══════════ TAB: Questions Editor ═══════════ -->
    <div v-show="activeTab === 'questions'" class="tab-content">
      <div class="card">
        <div class="card-row">
          <h2>Questions</h2>
          <div style="display:flex;gap:8px;">
            <div class="btn btn-primary pointer" @click="getQuestions">Reload</div>
            <div class="btn btn-green pointer" @click="saveQuestions" v-if="questionsLoaded">Save</div>
          </div>
        </div>

        <div v-if="!questionsLoaded" class="empty">
          Click "Reload" to load questions for editing.
        </div>

        <div v-else>
          <div
            v-for="(catQuestions, catKey) in questionsData.standard"
            :key="catKey"
            class="category"
          >
            <div class="category-header">
              <span class="category-badge">{{ catKey }}</span>
              <span>Übung {{ catKey }}</span>
            </div>
            <div class="category-body">
              <div v-for="(q, qIdx) in catQuestions" :key="qIdx" class="question-row">
                <span class="question-num">{{ qIdx + 1 }}.</span>
                <textarea
                  v-model="questionsData.standard[catKey][qIdx]"
                  rows="2"
                  class="question-input"
                ></textarea>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div id="version">v2.0</div>
  </div>
</template>

<script>
import copy from "copy-to-clipboard";

export default {
  name: "Admin",
  data() {
    return {
      password: "",
      roomCode: "",
      buyerToken: "",
      msg: "",
      msgTimer: null,
      players: [],
      rooms: [],
      activeTab: "setup",
      tabs: [
        { id: "setup", label: "Room Setup" },
        // { id: "rooms", label: "Rooms" },
        { id: "questions", label: "Questions" },
      ],
      questionsData: { standard: {}, alternative: [] },
      questionsLoaded: false,
    };
  },
  methods: {
    setMessage(msg) {
      this.msg = msg;
      if (this.msgTimer) clearTimeout(this.msgTimer);
      this.msgTimer = setTimeout(() => { this.msg = ""; }, 3000);
    },
    switchTab(id) {
      this.activeTab = id;
    },

    /* ── Room Setup ── */
    textFilter(text) {
      if (!text) return "";
      return "... " + text.substring(text.length - 20) + " ...";
    },
    addPlayer(n) {
      let current = this.players.length;
      for (let i = 0; i < n; i++) {
        this.players.push({ name: "#" + (current + 1), nameAlt: "", token: "" });
        current++;
      }
    },
    kopieren(i) {
      if (i == "GM") {
        copy(this.buyerToken);
        this.setMessage("Gamemaster link copied!");
        return;
      }
      copy(window.location.origin + "/#/player?token=" + this.players[i].token);
      this.setMessage("Player " + this.players[i].name + " link copied!");
    },
    erstellen() {
      this.buyerToken = "";
      if (this.roomCode.length < 2) return;
      let obj = {
        roomCode: this.roomCode,
        password: this.password,
        players: this.players,
      };
      this.$http.post("/createToken", obj).then((res) => {
        if (res.status === 200) {
          this.setMessage(res.data.msg);
          if (res.data.type == "success") {
            this.players = res.data.players;
            this.buyerToken =
              window.location.origin + "/#/gamemaster?token=" + res.data.access_token.buyer;
          }
        } else {
          this.setMessage("connection error");
        }
      }).catch((error) => {
        console.log(error);
        this.setMessage(error.response ? error.response.data.message : "Network Error");
      });
    },

    /* ── Rooms Overview ── */
    getRooms() {
      this.$http
        .post("/admin_getRooms", { password: this.password })
        .then((res) => {
          if (res.status === 200 && res.data.type == "success") {
            this.rooms = res.data.rooms;
            this.rooms.reverse();
            this.setMessage(this.rooms.length + " rooms loaded");
          } else {
            this.setMessage(res.data.msg || "Error loading rooms");
          }
        });
    },
    deleteRoom(id) {
      if (!confirm("CAUTION! Are you sure you want to delete room: " + id + "?")) return;
      this.$http
        .post("/deleteRoom", { searchId: id, password: this.password })
        .then((res) => {
          if (res.status === 200) {
            this.setMessage(res.data.msg);
            this.getRooms();
          } else {
            this.setMessage("connection error");
          }
        });
    },
    getScore(participants, index) {
      if (!participants[index]) return "PLAYER DELETED";
      return Math.round(participants[index].score);
    },
    answersGiven(players, index) {
      if (!players[index]) return "PLAYER DELETED";
      let answers = 0;
      for (const p in players) {
        for (const u in players[p]) {
          if (u == "score" || u == "winner" || u == "nameAlt") continue;
          for (const f in players[p][u]) {
            if (f == index) answers++;
          }
        }
      }
      return answers;
    },
    nameAlt(players, index) {
      if (index == "GM") return "";
      if (!players[index]) return "!NO PLAYER FOUND!";
      if (players[index].nameAlt) return players[index].nameAlt;
      return "";
    },
    timeJoined(start, joined) {
      var verstrichen = new Date(new Date(joined) - new Date(start));
      var min = verstrichen.getUTCMinutes();
      var sec = verstrichen.getUTCSeconds();
      return "+" + String(min).padStart(2, "0") + "min " + String(sec).padStart(2, "0") + "sec";
    },

    /* ── Questions Editor ── */
    getQuestions() {
      this.$http
        .post("/admin_getQuestions", { password: this.password })
        .then((res) => {
          if (res.status === 200 && res.data.type == "success") {
            this.questionsData = JSON.parse(JSON.stringify(res.data.questions));
            this.questionsLoaded = true;
            this.setMessage("Questions loaded");
          } else {
            this.setMessage(res.data.msg || "Error loading questions");
          }
        })
        .catch(() => this.setMessage("Connection error"));
    },
    saveQuestions() {
      this.$http
        .post("/admin_saveQuestions", {
          password: this.password,
          questions: this.questionsData,
        })
        .then((res) => {
          if (res.status === 200 && res.data.type == "success") {
            this.setMessage("Questions saved!");
          } else {
            this.setMessage(res.data.msg || "Error saving questions");
          }
        })
        .catch(() => this.setMessage("Connection error"));
    },
  },
  created() {},
};
</script>

<style scoped>
/* ── Base ── */
#Admin {
  max-width: 720px;
  margin: 0 auto;
  padding: 0 16px 60px;
  font-family: Calibri, sans-serif;
  color: #333;
}

/* ── Message Banner ── */
#messages {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  z-index: 100;
  background: var(--doblhoferRed, #ac0a42);
  color: white;
  text-align: center;
  padding: 14px 20px;
  font-size: 1.1em;
  font-weight: 600;
}

/* ── Header ── */
.admin-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 0;
  border-bottom: 3px solid #588fd3;
  margin-bottom: 0;
}

.admin-header h1 {
  font-size: 1.5em;
  color: #1860b9;
  margin: 0;
}

.pw-row {
  display: flex;
  align-items: center;
  gap: 6px;
}

.pw-row input {
  border: 1px solid #ccc;
  border-radius: 4px;
  padding: 6px 10px;
  font-size: 0.9em;
  width: 150px;
  font-family: Calibri, sans-serif;
}

.pw-row input:focus {
  outline: none;
  border-color: #588fd3;
  box-shadow: 0 0 0 2px rgba(88, 143, 211, 0.15);
}

/* ── Tab Navigation ── */
.tab-nav {
  display: flex;
  border-bottom: 2px solid #eee;
}

.tab {
  padding: 10px 20px;
  cursor: pointer;
  font-weight: 600;
  color: #888;
  border-bottom: 3px solid transparent;
  margin-bottom: -2px;
  transition: color 0.15s, border-color 0.15s;
}

.tab:hover {
  color: #333;
}

.tab.active {
  color: #1860b9;
  border-bottom-color: #588fd3;
}

/* ── Tab Content ── */
.tab-content {
  padding-top: 20px;
}

/* ── Card ── */
.card {
  background: #fff;
  border: 1px solid #e8e8e8;
  border-radius: 8px;
  padding: 24px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
}

.card h2 {
  font-size: 1.25em;
  color: #333;
  margin: 0 0 16px;
  padding-bottom: 8px;
  border-bottom: 1px solid #eee;
}

.card-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.card-row h2 {
  margin-bottom: 0;
  border-bottom: none;
  padding-bottom: 0;
}

/* ── Form Fields ── */
.field {
  margin-bottom: 16px;
}

.field label {
  display: block;
  font-weight: 600;
  color: #555;
  margin-bottom: 4px;
  font-size: 0.9em;
}

.field input {
  width: 100%;
  padding: 8px 10px;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 1em;
  font-family: Calibri, sans-serif;
  box-sizing: border-box;
}

.field input:focus {
  outline: none;
  border-color: #588fd3;
  box-shadow: 0 0 0 2px rgba(88, 143, 211, 0.15);
}

/* ── Buttons ── */
.btn {
  display: inline-block;
  padding: 8px 16px;
  border: 2px solid #588fd3;
  background: white;
  color: #588fd3;
  font-weight: 600;
  font-size: 0.9em;
  border-radius: 4px;
  text-align: center;
  transition: background 0.15s, color 0.15s;
  font-family: Calibri, sans-serif;
  user-select: none;
}

.btn:hover {
  background: #588fd3;
  color: white;
}

.btn-primary {
  background: #588fd3;
  color: white;
}

.btn-primary:hover {
  background: #1860b9;
  border-color: #1860b9;
}

.btn-green {
  border-color: var(--lightgreen, #279b3c);
  background: var(--lightgreen, #279b3c);
  color: white;
}

.btn-green:hover {
  background: var(--green, #2b6536);
  border-color: var(--green, #2b6536);
}

.btn-red {
  border-color: #c0392b;
  color: #c0392b;
}

.btn-red:hover {
  background: #c0392b;
  color: white;
}

.btn-sm {
  padding: 4px 10px;
  font-size: 0.82em;
}

.btn-wide {
  display: block;
  /* width: 100%; */
  padding: 12px;
  font-size: 1.05em;
  margin-top: 16px;
  letter-spacing: 0.5px;
}

.pointer { cursor: pointer; }

/* ── Player Buttons & List ── */
#PlayerButtonHolder {
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
  margin-bottom: 16px;
}

.hint {
  color: #888;
  font-size: 0.9em;
  margin-bottom: 10px;
}

.player-list {
  border: 1px solid #eee;
  border-radius: 6px;
  max-height: 350px;
  overflow-y: auto;
}

.player-row {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 12px;
  border-bottom: 1px solid #f5f5f5;
}

.player-row:last-child {
  border-bottom: none;
}

.player-id {
  font-weight: 700;
  color: #1860b9;
  min-width: 32px;
}

.player-name {
  color: #555;
}

.player-input {
  border: 1px solid #ddd;
  border-radius: 3px;
  padding: 4px 8px;
  font-size: 0.9em;
  width: 120px;
  font-family: Calibri, sans-serif;
}

.player-input:focus {
  outline: none;
  border-color: #588fd3;
}

.output {
  margin-left: auto;
  font-size: 0.85em;
  color: #888;
  font-family: monospace;
  padding: 2px 6px;
  border-radius: 3px;
}

.output:hover {
  background: var(--doblhoferRed, #ac0a42);
  color: white;
}

/* ── Gamemaster Link ── */
#buyer {
  margin-top: 16px;
  padding: 14px 18px;
  background: #1860b9;
  color: white;
  border-radius: 6px;
  cursor: pointer;
  word-break: break-all;
  transition: background 0.15s;
}

#buyer:hover {
  background: #104a8e;
}

.gm-title {
  font-weight: 700;
  font-size: 1.1em;
  margin-bottom: 4px;
  border-bottom: 2px solid rgba(255,255,255,0.3);
  padding-bottom: 4px;
}

.gm-code {
  font-family: monospace;
  font-size: 0.9em;
  opacity: 0.85;
}

/* ── Rooms Overview ── */
.empty {
  text-align: center;
  padding: 30px;
  color: #aaa;
}

.room-card {
  border: 1px solid #e8e8e8;
  border-radius: 6px;
  margin-top: 14px;
  overflow: hidden;
}

.room-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 14px;
  background: #f8f9fa;
  border-bottom: 1px solid #eee;
  flex-wrap: wrap;
  gap: 8px;
}

.room-date {
  font-size: 0.82em;
  color: #999;
  margin-left: 10px;
}

.room-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.badge {
  background: #e8f0fe;
  color: #1860b9;
  padding: 2px 8px;
  border-radius: 3px;
  font-size: 0.8em;
  font-weight: 600;
}

.participants {
  padding: 8px;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: 8px;
}

.participant {
  border: 1px solid #f0f0f0;
  border-radius: 4px;
  padding: 8px 10px;
  font-size: 0.88em;
  background: #fafafa;
}

.participant .accent {
  color: #1860b9;
}

.participant .detail {
  color: #777;
  font-size: 0.9em;
  line-height: 1.4;
}

.participant .detail strong {
  color: #333;
}

/* ── Questions Editor ── */
.category {
  border: 1px solid #e8e8e8;
  border-radius: 6px;
  margin-top: 14px;
  overflow: hidden;
}

.category-header {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 14px;
  background: #f8f9fa;
  font-weight: 600;
  border-bottom: 1px solid #eee;
}

.category-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 4px;
  background: #588fd3;
  color: white;
  font-weight: 700;
  font-size: 0.95em;
}

.category-body {
  padding: 12px 14px;
}

.question-row {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin-bottom: 10px;
}

.question-row:last-child {
  margin-bottom: 0;
}

.question-num {
  font-weight: 700;
  color: #999;
  min-width: 20px;
  padding-top: 8px;
}

.question-input {
  flex: 1;
  padding: 8px 10px;
  font-family: Calibri, sans-serif;
  font-size: 0.95em;
  border: 1px solid #ddd;
  border-radius: 4px;
  resize: vertical;
  min-height: 44px;
  line-height: 1.4;
}

.question-input:focus {
  outline: none;
  border-color: #588fd3;
  box-shadow: 0 0 0 2px rgba(88, 143, 211, 0.15);
}

/* ── Version ── */
#version {
  position: fixed;
  right: 8px;
  bottom: 4px;
  font-size: 0.75em;
  color: #ccc;
}
</style>
